import { useCallback, useMemo } from 'react';
import useLocalStorageState from 'hooks/useLocalStorageState';
import useShowAdmin from 'hooks/useShowAdmin';
import type { FilteredSurveyInput } from 'components/pages/Earn/__generated__/useEarnQuery.graphql';

interface FilterSetters {
  setWebcamSurveys: (value: boolean) => void;
  setPIISurveys: (value: boolean) => void;
  toggleFilter: (key: string) => void;
  filterActiveMap: Record<string, boolean>;
  applyFilterKeys: (keys: string[]) => void;
}

export default function useSurveyFilterInputs(): [FilteredSurveyInput, FilterSetters] {
  const [webcamSurveys, setWebcamSurveys] = useLocalStorageState('webcamSurveys', true);
  const [PIISurveys, setPIISurveys] = useLocalStorageState('PIISurveys', true);
  const isAdmin = useShowAdmin();

  const filterInputs = useMemo<FilteredSurveyInput>(
    () => ({
      webcamSurveys,
      PIISurveys,
      trackingParams: isAdmin ? [{ key: 'am', value: 'true' }] : [],
    }),
    [webcamSurveys, PIISurveys, isAdmin],
  );

  const filterActiveMap = useMemo<Record<string, boolean>>(
    () => ({
      webcam: webcamSurveys ?? false,
      pii: PIISurveys ?? false,
    }),
    [webcamSurveys, PIISurveys],
  );

  const toggleFilter = useCallback(
    (key: string) => {
      const dispatch: Record<string, () => void> = {
        webcam: () => setWebcamSurveys(!(webcamSurveys ?? true)),
        pii: () => setPIISurveys(!(PIISurveys ?? true)),
      };
      dispatch[key]?.();
    },
    [webcamSurveys, PIISurveys, setWebcamSurveys, setPIISurveys],
  );

  const applyFilterKeys = useCallback(
    (keys: string[]) => {
      const apply: Record<string, (value: boolean) => void> = {
        webcam: setWebcamSurveys,
        pii: setPIISurveys,
      };
      Object.keys(apply).forEach((key) => apply[key]?.(keys.includes(key)));
    },
    [setWebcamSurveys, setPIISurveys],
  );

  const setters = useMemo(
    () => ({ setWebcamSurveys, setPIISurveys, toggleFilter, filterActiveMap, applyFilterKeys }),
    [setWebcamSurveys, setPIISurveys, toggleFilter, filterActiveMap, applyFilterKeys],
  );

  return [filterInputs, setters];
}
